import { useState, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { portalKnowledgeApi } from '../../services/api';
import { usePortalStore, toast } from '../../stores';
import {
  NexusCard, NexusButton, NexusInput, NexusTextarea,
  NexusModal, NexusModalFooter, NexusBadge,
} from '../../components/nexus-ui';
import {
  Plus, Database, Trash2, FileText, Upload, CheckCircle2,
  AlertCircle, Loader2, HardDrive, X, File,
} from 'lucide-react';
import { KBDrawingsPanel } from '../../components/knowledge/KBDrawingsPanel';

const FILE_STATUS = {
  queued: { label: 'Queued', color: 'text-nexus-text-tertiary', bg: 'bg-white/5' },
  uploading: { label: 'Uploading', color: 'text-nexus-accent-blue', bg: 'bg-nexus-accent-blue/10' },
  parsing: { label: 'Parsing', color: 'text-nexus-accent-amber', bg: 'bg-nexus-accent-amber/10' },
  embedding: { label: 'Embedding', color: 'text-nexus-accent-violet', bg: 'bg-nexus-accent-violet/10' },
  completed: { label: 'Ready', color: 'text-nexus-accent-mint', bg: 'bg-nexus-accent-mint/10' },
  error: { label: 'Failed', color: 'text-red-400', bg: 'bg-red-500/10' },
};

// Map server-side ingestion job status → existing UI stage.
const STATUS_TO_STAGE = {
  queued: 'queued', reading: 'uploading', parsing: 'parsing',
  chunking: 'parsing', embedding: 'embedding',
  completed: 'completed', failed: 'error',
};

const PortalKnowledge = () => {
  const queryClient = useQueryClient();
  const { tenant, user } = usePortalStore();
  // Read-only mode for org members — they can browse KBs & chunks but
  // can't create / delete / upload. Super-admins and personal-tenant
  // owners (single seat) get full access.
  const canManage = (user?.role === 'admin') || !!user?.is_super_admin || tenant?.account_type === 'personal';
  const [selectedKB, setSelectedKB] = useState(null);
  // Sub-tab within a KB: 'chunks' (existing text/doc UI) or 'drawings' (new).
  const [kbSubTab, setKbSubTab] = useState('chunks');
  const [showCreateKB, setShowCreateKB] = useState(false);
  const [showAddChunk, setShowAddChunk] = useState(false);
  const [newKB, setNewKB] = useState({ name: '', description: '' });
  const [newChunk, setNewChunk] = useState({ content: '', source_file: '' });
  const [fileQueue, setFileQueue] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const { data: kbs, isLoading: kbsLoading } = useQuery({
    queryKey: ['portalKnowledge'],
    queryFn: () => portalKnowledgeApi.list().then(r => r.data),
  });

  const { data: chunks } = useQuery({
    queryKey: ['portalChunks', selectedKB?.id],
    queryFn: () => portalKnowledgeApi.listChunks(selectedKB.id).then(r => r.data),
    enabled: !!selectedKB?.id,
  });

  // Active ingestion jobs across all the tenant's KBs — drives the
  // "Ingesting…" pill on each KB card. Auto-refetches every 3 s.
  const { data: activeJobs = [] } = useQuery({
    queryKey: ['kbActiveJobs'],
    queryFn: () => portalKnowledgeApi.listIngestionJobs({ onlyActive: true }).then(r => r.data),
    refetchInterval: 3000,
    staleTime: 0,
  });
  const activeByKb = (activeJobs || []).reduce((acc, j) => {
    acc[j.kb_id] = (acc[j.kb_id] || 0) + 1; return acc;
  }, {});

  const createKBMutation = useMutation({
    mutationFn: (data) => portalKnowledgeApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portalKnowledge'] });
      toast.success('Knowledge base created');
      setShowCreateKB(false);
      setNewKB({ name: '', description: '' });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create'),
  });

  const addChunkMutation = useMutation({
    mutationFn: ({ kbId, data }) => portalKnowledgeApi.addChunk(kbId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portalChunks', selectedKB?.id] });
      queryClient.invalidateQueries({ queryKey: ['portalKnowledge'] });
      toast.success('Knowledge added');
      setShowAddChunk(false);
      setNewChunk({ content: '', source_file: '' });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to add'),
  });

  const deleteKBMutation = useMutation({
    mutationFn: (kbId) => portalKnowledgeApi.delete(kbId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portalKnowledge'] });
      setSelectedKB(null);
      toast.success('Knowledge base deleted');
    },
  });

  // File upload handler — uploads to the server which returns a job_id
  // immediately. The browser then polls `getIngestionJob` until the job
  // reaches completed / failed. This sidesteps every ingress timeout —
  // parsing + embedding happen entirely on the backend's event loop.
  const handleFiles = useCallback((files) => {
    if (!selectedKB?.id) {
      toast.error('Select a knowledge base first');
      return;
    }
    const newFiles = Array.from(files).map(file => ({
      id: Math.random().toString(36).slice(2),
      file, name: file.name, size: file.size,
      status: 'queued', progress: 0,
    }));
    setFileQueue(prev => [...prev, ...newFiles]);

    newFiles.forEach(async (entry) => {
      setFileQueue(prev => prev.map(pf => pf.id === entry.id ? { ...pf, status: 'uploading', progress: 5 } : pf));
      try {
        const onUploadProgress = (e) => {
          if (!e.total) return;
          const pct = Math.min(50, Math.round((e.loaded / e.total) * 50));
          setFileQueue(prev => prev.map(pf => pf.id === entry.id ? { ...pf, progress: 5 + pct } : pf));
        };
        const res = await portalKnowledgeApi.upload(selectedKB.id, entry.file, onUploadProgress);
        const jobId = res?.data?.job_id;
        if (!jobId) throw new Error('No job_id from server');

        setFileQueue(prev => prev.map(pf => pf.id === entry.id ? {
          ...pf, status: 'parsing', progress: 55, jobId,
        } : pf));

        // Poll the job until terminal.
        const pollOnce = async () => {
          try {
            const jr = await portalKnowledgeApi.getIngestionJob(jobId);
            const j = jr.data;
            const stage = STATUS_TO_STAGE[j.status] || 'parsing';
            setFileQueue(prev => prev.map(pf => pf.id === entry.id ? {
              ...pf,
              status: stage,
              progress: Math.max(pf.progress || 0, j.progress || 0),
              chunks: j.chunks_created || j.chunks_embedded || 0,
              tokens: j.total_tokens || 0,
              error: j.error || pf.error,
            } : pf));
            if (j.status === 'completed' || j.status === 'failed') {
              queryClient.invalidateQueries({ queryKey: ['portalChunks', selectedKB.id] });
              queryClient.invalidateQueries({ queryKey: ['portalKnowledge'] });
              queryClient.invalidateQueries({ queryKey: ['kbActiveJobs'] });
              if (j.status === 'failed') {
                toast.error(`${entry.name}: ${j.error || 'Ingestion failed'}`);
              }
              return true;
            }
          } catch (err) {
            // Network hiccup — keep trying for a while.
            console.warn('[kb-ingest] poll error', err?.message);
          }
          return false;
        };
        const start = Date.now();
        // Cap at 2 hours of polling — anything longer is a server-side
        // hang we'd want surfaced anyway.
        const maxMs = 2 * 60 * 60 * 1000;
        while (Date.now() - start < maxMs) {
          const done = await pollOnce();
          if (done) break;
          await new Promise(r => setTimeout(r, 2000));
        }
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || 'Upload failed';
        toast.error(`${entry.name}: ${detail}`);
        setFileQueue(prev => prev.map(pf => pf.id === entry.id ? { ...pf, status: 'error', progress: 100, error: detail } : pf));
      }
    });
  }, [selectedKB, queryClient]);

  const removeFile = (id) => setFileQueue(prev => prev.filter(f => f.id !== id));

  // Drag handlers
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  };

  // Storage usage
  const storageUsed = tenant?.storage_used_bytes || 0;
  const storageMax = tenant?.max_storage_bytes || 500 * 1024 * 1024;
  const storagePercent = Math.min((storageUsed / storageMax) * 100, 100);
  const storageColor = storagePercent > 80 ? 'bg-red-500' : storagePercent > 60 ? 'bg-nexus-accent-amber' : 'bg-nexus-accent-mint';

  return (
    <div className="space-y-6" data-testid="portal-knowledge">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-nexus-text-primary">Knowledge Base</h2>
          <p className="text-nexus-text-secondary text-sm mt-1">
            {canManage ? 'Upload documents to train your AI assistant' : 'Read-only view — ask your organization admin to upload documents'}
          </p>
        </div>
        {canManage && (
          <NexusButton onClick={() => setShowCreateKB(true)} data-testid="create-kb-btn">
            <Plus size={16} className="mr-1" /> New Knowledge Base
          </NexusButton>
        )}
      </div>

      {/* Storage Usage Bar */}
      <NexusCard variant="glass" hover={false} className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <HardDrive size={16} className="text-nexus-text-tertiary" />
            <span className="text-sm text-nexus-text-secondary">Storage</span>
          </div>
          <span className="text-xs text-nexus-text-tertiary">
            {formatBytes(storageUsed)} / {formatBytes(storageMax)}
          </span>
        </div>
        <div className="h-2 bg-nexus-bg-tertiary rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${storagePercent}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className={`h-full rounded-full ${storageColor} ${storagePercent > 90 ? 'storage-critical' : ''}`}
          />
        </div>
      </NexusCard>

      {/* File Upload Drop Zone — admins only */}
      {canManage && (
      <motion.div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        animate={isDragging ? { scale: 1.01, borderColor: 'rgba(30, 78, 140,0.5)' } : { scale: 1, borderColor: 'rgba(255,255,255,0.08)' }}
        className={`relative rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
          isDragging ? 'bg-nexus-accent-mint/5' : 'bg-transparent hover:bg-nexus-bg-tertiary/20'
        } ${isDragging ? 'dropzone-animated-border' : ''}`}
        onClick={() => fileInputRef.current?.click()}
        data-testid="file-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".txt,.md,.markdown,.rst,.log,.pdf,.csv,.tsv,.json,.xml,.yaml,.yml,.toml,.ini,.cfg,.conf,.html,.htm,.doc,.docx,.rtf,.odt,.xls,.xlsx,.ods,.ppt,.pptx,.odp,.mpp,.mpx,.mpd,.mspdi,.xer"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
          className="hidden"
        />
        <motion.div
          animate={isDragging ? { y: -4 } : { y: 0 }}
          className="flex flex-col items-center"
        >
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
            isDragging ? 'bg-nexus-accent-mint/20' : 'bg-white/5'
          }`}>
            <Upload size={22} className={isDragging ? 'text-nexus-accent-mint' : 'text-nexus-text-tertiary'} />
          </div>
          <p className="text-sm text-nexus-text-primary font-medium">
            {isDragging ? 'Drop files here' : 'Drag & drop files or click to browse'}
          </p>
          <p className="text-xs text-nexus-text-tertiary mt-1">
            PDF · DOC/DOCX · XLS/XLSX · PPT/PPTX · CSV · TSV · TXT · MD · JSON · XML · YAML · HTML · ODT/ODS/ODP · RTF
          </p>
          {!selectedKB?.id && (
            <p className="text-[11px] text-nexus-accent-amber/80 mt-2">
              Select or create a knowledge base below first.
            </p>
          )}
        </motion.div>
      </motion.div>
      )}

      {/* File Queue */}
      <AnimatePresence>
        {fileQueue.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-2">
            {fileQueue.map((f) => {
              const status = FILE_STATUS[f.status] || FILE_STATUS.queued;
              return (
                <motion.div
                  key={f.id}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={f.status === 'error' ? { opacity: 1, y: 0, scale: 1, x: [0, -5, 5, -5, 5, 0] } : { opacity: 1, y: 0, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                  className="flex items-center gap-3 p-3 bg-nexus-bg-secondary/60 border border-nexus-border-subtle rounded-xl"
                  data-testid={`file-${f.id}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${status.bg}`}>
                    {f.status === 'completed' ? (
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400 }}>
                        <CheckCircle2 size={18} className="text-nexus-accent-mint" />
                      </motion.div>
                    ) : f.status === 'error' ? (
                      <AlertCircle size={18} className="text-red-400" />
                    ) : f.status === 'queued' ? (
                      <File size={18} className="text-nexus-text-tertiary" />
                    ) : (
                      <Loader2 size={18} className={`${status.color} animate-spin`} />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-nexus-text-primary truncate">{f.name}</p>
                      <NexusBadge variant={f.status === 'completed' ? 'success' : f.status === 'error' ? 'error' : 'default'}>
                        {status.label}
                      </NexusBadge>
                    </div>
                    <div className="h-1.5 bg-nexus-bg-tertiary rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${f.progress}%` }}
                        className={`h-full rounded-full ${
                          f.status === 'completed' ? 'bg-nexus-accent-mint'
                            : f.status === 'error' ? 'bg-red-500'
                            : 'bg-nexus-accent-blue animate-shimmer'
                        }`}
                      />
                    </div>
                    <p className="text-[11px] text-nexus-text-tertiary mt-0.5">{formatBytes(f.size)}</p>
                  </div>

                  <button onClick={() => removeFile(f.id)} className="p-1 text-nexus-text-tertiary hover:text-nexus-text-secondary">
                    <X size={14} />
                  </button>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Knowledge Base Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {kbsLoading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="h-28 bg-nexus-bg-tertiary rounded-xl animate-shimmer" />
          ))
        ) : kbs?.length > 0 ? (
          kbs.map((kb, i) => (
            <motion.div
              key={kb.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <NexusCard
                variant="glass"
                onClick={() => setSelectedKB(selectedKB?.id === kb.id ? null : kb)}
                className={`cursor-pointer p-4 transition-all ${
                  selectedKB?.id === kb.id ? 'ring-1 ring-nexus-accent-mint shadow-lg shadow-nexus-accent-mint/5' : ''
                }`}
                data-testid={`kb-card-${kb.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-nexus-accent-violet/10 flex items-center justify-center">
                      <Database size={20} className="text-nexus-accent-violet" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-nexus-text-primary">{kb.name}</h3>
                      <p className="text-xs text-nexus-text-tertiary mt-0.5">{kb.chunk_count || 0} chunks</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {activeByKb[kb.id] > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-sky-300 bg-sky-500/10 border border-sky-400/25"
                        title={`${activeByKb[kb.id]} file${activeByKb[kb.id] === 1 ? '' : 's'} ingesting in the background`}
                        data-testid={`kb-ingesting-pill-${kb.id}`}
                      >
                        <Loader2 size={9} className="animate-spin" strokeWidth={2.5} />
                        Ingesting {activeByKb[kb.id] > 1 ? `· ${activeByKb[kb.id]}` : ''}
                      </span>
                    )}
                    <NexusBadge variant="success">{kb.status || 'active'}</NexusBadge>
                  </div>
                </div>
              </NexusCard>
            </motion.div>
          ))
        ) : (
          <NexusCard variant="glass" hover={false} className="col-span-full p-8 text-center">
            <Database size={32} className="text-nexus-text-tertiary mx-auto mb-3" />
            <p className="text-nexus-text-secondary text-sm">No knowledge bases yet. Create one above.</p>
          </NexusCard>
        )}
      </div>

      {/* Chunks View */}
      <AnimatePresence>
        {selectedKB && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
            <NexusCard variant="glass" hover={false}>
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-base font-semibold text-nexus-text-primary">{selectedKB.name}</h3>
                    <p className="text-xs text-nexus-text-tertiary">{selectedKB.description}</p>
                  </div>
                  <div className="flex gap-2">
                    {canManage && (
                      <>
                        <NexusButton size="sm" onClick={() => setShowAddChunk(true)} data-testid="add-chunk-btn">
                          <Plus size={14} className="mr-1" /> Add
                        </NexusButton>
                        <NexusButton
                          size="sm"
                          variant="danger"
                          onClick={() => { if (window.confirm('Delete?')) deleteKBMutation.mutate(selectedKB.id); }}
                        >
                          <Trash2 size={14} />
                        </NexusButton>
                      </>
                    )}
                  </div>
                </div>

                {/* Tab switcher — text chunks vs engineering drawings */}
                <div className="flex items-center gap-1.5 mb-4 border-b" style={{ borderColor: 'var(--hairline)' }}>
                  {[
                    { id: 'chunks', label: 'Text & Documents' },
                    { id: 'drawings', label: 'Drawings' },
                  ].map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setKbSubTab(t.id)}
                      className="px-3 py-2 text-[12px] font-semibold transition-all"
                      style={{
                        borderBottom: `2px solid ${kbSubTab === t.id ? '#F26B1F' : 'transparent'}`,
                        color: kbSubTab === t.id ? '#F26B1F' : 'var(--text-tertiary)',
                        marginBottom: '-1px',
                      }}
                      data-testid={`kb-subtab-${t.id}`}>
                      {t.label}
                    </button>
                  ))}
                </div>

                {kbSubTab === 'drawings' ? (
                  <KBDrawingsPanel kbId={selectedKB.id} readOnly={!canManage} />
                ) : chunks?.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {chunks.map((chunk, i) => (
                      <motion.div
                        key={chunk.id || i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.03 }}
                        className="p-3 bg-nexus-bg-tertiary/30 rounded-lg border border-nexus-border-subtle"
                      >
                        <p className="text-sm text-nexus-text-primary line-clamp-2">{chunk.content}</p>
                        <div className="flex items-center gap-3 mt-2">
                          {chunk.source_file && (
                            <span className="flex items-center gap-1 text-xs text-nexus-text-tertiary">
                              <FileText size={12} /> {chunk.source_file}
                            </span>
                          )}
                          <span className="text-xs text-nexus-text-tertiary">{chunk.token_count} tokens</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-nexus-text-tertiary text-center py-6">No content added yet</p>
                )}
              </div>
            </NexusCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create KB Modal */}
      <NexusModal isOpen={showCreateKB} onClose={() => setShowCreateKB(false)} title="Create Knowledge Base">
        <div className="space-y-4">
          <NexusInput
            label="Name"
            value={newKB.name}
            onChange={(e) => setNewKB(p => ({ ...p, name: e.target.value }))}
            placeholder="e.g., Project Documents"
            data-testid="kb-name-input"
          />
          <NexusTextarea
            label="Description"
            value={newKB.description}
            onChange={(e) => setNewKB(p => ({ ...p, description: e.target.value }))}
            placeholder="What kind of knowledge will this contain?"
            rows={3}
          />
        </div>
        <NexusModalFooter>
          <NexusButton variant="ghost" onClick={() => setShowCreateKB(false)}>Cancel</NexusButton>
          <NexusButton onClick={() => createKBMutation.mutate(newKB)} isLoading={createKBMutation.isPending} data-testid="create-kb-submit">
            Create
          </NexusButton>
        </NexusModalFooter>
      </NexusModal>

      {/* Add Chunk Modal — paste text OR upload file */}
      <NexusModal isOpen={showAddChunk} onClose={() => setShowAddChunk(false)} title="Add Knowledge Content" size="lg">
        <div className="space-y-4">
          {/* Quick file upload row */}
          <div>
            <p className="text-[11px] uppercase tracking-wider text-nexus-text-tertiary mb-1.5">Upload a file</p>
            <label
              htmlFor="kb-chunk-file"
              className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-nexus-border-subtle bg-nexus-bg-tertiary/30 hover:bg-nexus-bg-tertiary/50 hover:border-nexus-accent-mint/30 px-4 py-3 cursor-pointer transition-colors"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-md bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Upload size={14} className="text-nexus-text-tertiary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-nexus-text-primary font-medium">
                    Click to choose a file
                  </p>
                  <p className="text-[10px] text-nexus-text-tertiary truncate">
                    PDF · DOC/DOCX · XLS/XLSX · PPT/PPTX · CSV · JSON · XML · YAML · HTML · TXT · MD · RTF · ODT/ODS/ODP
                  </p>
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-nexus-accent-mint">
                Browse
              </span>
            </label>
            <input
              id="kb-chunk-file"
              type="file"
              accept=".txt,.md,.markdown,.rst,.log,.pdf,.csv,.tsv,.json,.xml,.yaml,.yml,.toml,.ini,.cfg,.conf,.html,.htm,.doc,.docx,.rtf,.odt,.xls,.xlsx,.ods,.ppt,.pptx,.odp,.mpp,.mpx,.mpd,.mspdi,.xer"
              className="hidden"
              data-testid="add-chunk-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (!f) return;
                if (!selectedKB?.id) { toast.error('Select a knowledge base first'); return; }
                handleFiles([f]);
                setShowAddChunk(false);  // file uploads stream in the queue
              }}
            />
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-nexus-border-subtle"></div></div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-wider">
              <span className="bg-nexus-bg-secondary px-2 text-nexus-text-tertiary">or paste raw text</span>
            </div>
          </div>

          <NexusInput
            label="Source (optional)"
            value={newChunk.source_file}
            onChange={(e) => setNewChunk(p => ({ ...p, source_file: e.target.value }))}
            placeholder="e.g., project_specs.pdf"
          />
          <NexusTextarea
            label="Content"
            value={newChunk.content}
            onChange={(e) => setNewChunk(p => ({ ...p, content: e.target.value }))}
            placeholder="Paste your knowledge content here..."
            rows={10}
            data-testid="chunk-content-input"
          />
        </div>
        <NexusModalFooter>
          <NexusButton variant="ghost" onClick={() => setShowAddChunk(false)}>Cancel</NexusButton>
          <NexusButton
            onClick={() => addChunkMutation.mutate({ kbId: selectedKB.id, data: newChunk })}
            isLoading={addChunkMutation.isPending}
            disabled={!newChunk.content.trim()}
            data-testid="add-chunk-submit"
          >
            Add Content
          </NexusButton>
        </NexusModalFooter>
      </NexusModal>
    </div>
  );
};

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default PortalKnowledge;
